import { API_URL, CLIENT_RELEASE, DEPLOY_CONTEXT } from '@/modules/common/config'
import store from '@/store'

const SESSION_ID_KEY = 'session-id'

let currentSessionId: string | undefined

// Not crypto.randomUUID(): that exists only on HTTPS and localhost, and some local setups serve plain HTTP.
function randomId(): string {
	const bytes = crypto.getRandomValues(new Uint8Array(16))

	return Array.from(bytes, byte => byte.toString(16).padStart(2, '0')).join('')
}

function storedOrNewSessionId(): string {
	try {
		const storedSessionId = sessionStorage.getItem(SESSION_ID_KEY)

		if (storedSessionId) {
			return storedSessionId
		}

		const newSessionId = randomId()
		sessionStorage.setItem(SESSION_ID_KEY, newSessionId)

		return newSessionId
	} catch {
		// Storage can be blocked, as in the insurance wizard's cross-site iframe; the id then lasts one page load.
		return randomId()
	}
}

// Kept in sessionStorage, so a reload or the round trip through Auth0 stays one session.
function sessionId(): string {
	currentSessionId ??= storedOrNewSessionId()

	return currentSessionId
}

// The AD puts these into every log line the request writes, and into the jobs it queues.
export function clientDashboardHeaders(): Record<string, string> {
	const headers: Record<string, string> = { 'Session-Id': sessionId() }

	if (CLIENT_RELEASE !== null) {
		headers['Client-Release'] = CLIENT_RELEASE
	}

	if (DEPLOY_CONTEXT !== null) {
		headers['Client-Deploy-Context'] = DEPLOY_CONTEXT
	}

	return headers
}

export function authorizationHeaders(): Record<string, string> {
	const { token, useOldAuthentication } = store.state.auth

	if (!token) {
		return {}
	}

	if (useOldAuthentication) {
		return { Authorization: `Bearer ${token}`, 'Use-Old-Authentication': 'true' }
	}

	return { Authorization: `Bearer ${token}` }
}

// Not axios: 0.28 sends through XHR, which a closing page cancels. Move onto axios's fetch adapter once on 1.7+.
export function keepaliveRequest(path: string, body: unknown): Promise<Response> {
	return fetch(`${API_URL}/${path}`, {
		method: 'POST',
		keepalive: true,
		headers: {
			'Content-Type': 'application/json',
			...clientDashboardHeaders(),
			...authorizationHeaders(),
		},
		body: JSON.stringify(body),
	})
}
